import type { CSSProperties } from 'react';
import { CarouselStylePreset, publicAssetUrl, weirdphysicsPreset } from './presets';

export type TextDensity = 'dramatic' | 'balanced' | 'compact';
export type GenericCarouselLayoutVariant =
  | 'image-led'
  | 'poster'
  | 'no-image-typography'
  | 'chat-overlay'
  | 'split-comparison'
  | 'editorial-card';

export const GENERIC_CAROUSEL_LAYOUT_VARIANTS: Array<{ id: GenericCarouselLayoutVariant; label: string }> = [
  { id: 'image-led', label: 'Image-led' },
  { id: 'poster', label: 'Poster' },
  { id: 'no-image-typography', label: 'No-image typography' },
  { id: 'chat-overlay', label: 'Chat overlay' },
  { id: 'split-comparison', label: 'Split comparison' },
  { id: 'editorial-card', label: 'Editorial card' },
];

export interface GenericCarouselBrandPresetValues {
  layoutVariant?: GenericCarouselLayoutVariant;
  brandTag?: string;
  logoUrl?: string;
  highlightTextureUrl?: string;
  headlineFontId?: string;
  bodyFontId?: string;
  highlightStart?: string;
  highlightEnd?: string;
  highlightIntensity?: number;
  highlightTextureStrength?: number;
  bottomOverlayStrength?: number;
  textDensity?: TextDensity;
  showDecorativeLines?: boolean;
}

export interface GenericCarouselBrandPreset {
  id: string;
  clipStyle: 'genericCarousel';
  name: string;
  brandTag: string;
  values: GenericCarouselBrandPresetValues;
  readOnly?: boolean;
}

export const GENERIC_CAROUSEL_FONT_OPTIONS = [
  { id: 'impact', label: 'Impact', family: 'GenericCarouselImpact' },
  { id: 'systemBlack', label: 'System Black', family: 'Arial Black' },
  { id: 'interItalic', label: 'Inter Italic', family: 'GenericCarouselInterItalic' },
];

const headlineFontFamily = (fontId?: string): string => {
  if (fontId === 'interItalic') return 'GenericCarouselInterItalic';
  if (fontId === 'systemBlack') return '"Arial Black", "Helvetica Neue", Arial, sans-serif';
  return 'GenericCarouselImpact';
};

const bodyFontFamily = (fontId?: string): string => {
  if (fontId === 'impact') return 'GenericCarouselImpact';
  if (fontId === 'systemBlack') return '"Arial Black", "Helvetica Neue", Arial, sans-serif';
  return 'GenericCarouselInterItalic';
};

const densityScale = (density?: TextDensity) => {
  if (density === 'dramatic') return { headline: 1.08, body: 0.94 };
  if (density === 'compact') return { headline: 0.9, body: 1.08 };
  return { headline: 1, body: 1 };
};

const clamp = (value: unknown, fallback: number, min: number, max: number): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(max, Math.max(min, numeric));
};

const colorValue = (value: unknown, fallback: string): string =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value.trim()) ? value.trim() : fallback;

const textureContrast = (value: unknown): number => 1 + Math.max(0, clamp(value, 1, 0, 1.5) - 1) * 0.35;

const layoutVariant = (value: unknown): GenericCarouselLayoutVariant => {
  const match = GENERIC_CAROUSEL_LAYOUT_VARIANTS.find((variant) => variant.id === value);
  return match?.id || 'image-led';
};

const cloneValues = (values: GenericCarouselBrandPresetValues): GenericCarouselBrandPresetValues => ({
  ...values,
});

export const weirdphysicsBrandPreset: GenericCarouselBrandPreset = {
  id: 'weirdphysics',
  clipStyle: 'genericCarousel',
  name: 'WeirdPhysics',
  brandTag: 'weirdphysics',
  readOnly: true,
  values: {
    layoutVariant: 'image-led',
    brandTag: 'weirdphysics',
    logoUrl: weirdphysicsPreset.assets.frontLogo,
    highlightTextureUrl: weirdphysicsPreset.assets.gradient,
    headlineFontId: 'impact',
    bodyFontId: 'interItalic',
    highlightStart: weirdphysicsPreset.colors.accent,
    highlightEnd: weirdphysicsPreset.colors.highlightEnd,
    highlightIntensity: weirdphysicsPreset.effects.highlightIntensity,
    highlightTextureStrength: weirdphysicsPreset.effects.highlightTextureStrength,
    bottomOverlayStrength: weirdphysicsPreset.effects.bottomOverlayStrength,
    textDensity: 'balanced',
    showDecorativeLines: true,
  },
};

export const premiumPhysicsEditorialBrandPreset: GenericCarouselBrandPreset = {
  id: 'premium-physics-editorial',
  clipStyle: 'genericCarousel',
  name: 'Neon Hook Poster',
  brandTag: 'weirdphysics',
  readOnly: true,
  values: {
    layoutVariant: 'poster',
    brandTag: 'weirdphysics',
    highlightTextureUrl: '/remotion/generic-carousel/templates/premium-physics-editorial/highlight-texture.svg',
    headlineFontId: 'systemBlack',
    bodyFontId: 'interItalic',
    highlightStart: '#6EE7FF',
    highlightEnd: '#FFFFFF',
    highlightIntensity: 1.18,
    highlightTextureStrength: 0.28,
    bottomOverlayStrength: 1.22,
    textDensity: 'dramatic',
    showDecorativeLines: true,
  },
};

export const builtinGenericCarouselBrandPresets = [
  weirdphysicsBrandPreset,
  premiumPhysicsEditorialBrandPreset,
];

export const getBuiltinGenericCarouselBrandPreset = (id: string): GenericCarouselBrandPreset | undefined =>
  builtinGenericCarouselBrandPresets.find((preset) => preset.id === id);

export const buildCarouselPresetFromDraft = (
  draft?: GenericCarouselBrandPresetValues | null,
  fallbackBrandTag = 'weirdphysics'
): CarouselStylePreset => {
  const values = draft || {};
  const density = densityScale(values.textDensity);
  const logoUrl = typeof values.logoUrl === 'string' && values.logoUrl.trim()
    ? values.logoUrl.trim()
    : weirdphysicsPreset.assets.frontLogo;
  const textureUrl =
    typeof values.highlightTextureUrl === 'string' && values.highlightTextureUrl.trim()
      ? values.highlightTextureUrl.trim()
      : weirdphysicsPreset.assets.gradient;

  return {
    ...weirdphysicsPreset,
    layoutVariant: layoutVariant(values.layoutVariant),
    id: String(values.brandTag || fallbackBrandTag || weirdphysicsPreset.id).toLowerCase().replace(/\s+/g, '-'),
    fonts: {
      headline: headlineFontFamily(values.headlineFontId),
      body: bodyFontFamily(values.bodyFontId),
    },
    colors: {
      ...weirdphysicsPreset.colors,
      accent: colorValue(values.highlightStart, weirdphysicsPreset.colors.accent),
      highlightEnd: colorValue(values.highlightEnd, weirdphysicsPreset.colors.highlightEnd),
    },
    assets: {
      ...weirdphysicsPreset.assets,
      frontLogo: logoUrl,
      gradient: textureUrl,
    },
    headline: {
      ...weirdphysicsPreset.headline,
      minFontSize: Math.round(weirdphysicsPreset.headline.minFontSize * density.headline),
      maxFontSize: Math.round(weirdphysicsPreset.headline.maxFontSize * density.headline),
    },
    explanation: {
      ...weirdphysicsPreset.explanation,
      minFontSize: Math.round(weirdphysicsPreset.explanation.minFontSize * density.body),
      maxFontSize: Math.round(weirdphysicsPreset.explanation.maxFontSize * density.body),
    },
    features: {
      ...weirdphysicsPreset.features,
      showDecorativeLines:
        typeof values.showDecorativeLines === 'boolean'
          ? values.showDecorativeLines
          : weirdphysicsPreset.features.showDecorativeLines,
    },
    effects: {
      highlightIntensity: clamp(values.highlightIntensity, weirdphysicsPreset.effects.highlightIntensity, 0.75, 1.8),
      highlightTextureStrength: clamp(
        values.highlightTextureStrength,
        weirdphysicsPreset.effects.highlightTextureStrength,
        0,
        1.5
      ),
      bottomOverlayStrength: clamp(
        values.bottomOverlayStrength,
        weirdphysicsPreset.effects.bottomOverlayStrength,
        0.35,
        1.35
      ),
    },
  };
};

export const loadBrandPresetDraft = (
  metadata: Record<string, any>,
  preset: GenericCarouselBrandPreset
): Record<string, any> => {
  const draft = {
    ...cloneValues(preset.values),
    brandTag: preset.values.brandTag || preset.brandTag,
  };
  return {
    ...metadata,
    brandingTag: draft.brandTag || preset.brandTag,
    stylePresetId: preset.id,
    stylePresetName: preset.name,
    stylePresetDirty: true,
    stylePresetDraft: draft,
  };
};

export const updateBrandPresetDraftValue = (
  metadata: Record<string, any>,
  key: keyof GenericCarouselBrandPresetValues,
  value: GenericCarouselBrandPresetValues[typeof key]
): Record<string, any> => {
  const currentDraft =
    metadata.stylePresetDraft && typeof metadata.stylePresetDraft === 'object'
      ? metadata.stylePresetDraft
      : {};
  const nextDraft = {
    ...currentDraft,
    [key]: value,
  };
  return {
    ...metadata,
    ...(key === 'brandTag' ? { brandingTag: String(value || '') } : {}),
    stylePresetDirty: true,
    stylePresetDraft: nextDraft,
  };
};

export const buildHighlightBackground = ({
  textureUrl,
  highlightStart,
  highlightEnd,
  highlightTextureStrength,
}: {
  textureUrl: string;
  highlightStart: string;
  highlightEnd: string;
  highlightTextureStrength: number;
}): CSSProperties => ({
  backgroundImage: `linear-gradient(180deg, ${highlightEnd} 0%, ${highlightStart} 52%, ${highlightEnd} 100%), url(${publicAssetUrl(textureUrl)})`,
  backgroundBlendMode: 'color',
  backgroundPosition: 'center',
  backgroundSize: '100% 100%, 100% 100%',
  filter: `contrast(${textureContrast(highlightTextureStrength)})`,
});
